import { BaseViewModel } from "@iot-platform/core";
import type { YAxes } from "@station/domain/models/sensor-group.model";
import { SensorGroups } from "@station/domain/models/sensor-group.model";
import type { Sensor } from "@station/domain/models/sensor.model.ts";
import type { Station } from "@station/domain/models/station.model";
import { SensorAlertGetAllUseCase } from "@station/domain/usecases/sensor-alert-get-all.usecase";
import { SensorGroupGetAllUseCase } from "@station/domain/usecases/sensor-group-get-all.usecase";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

export interface ModalGraphProps {
    visible: boolean;
    stationId?: number;
    sensors: Readonly<Sensor>[];
    yAxes: Readonly<YAxes>[];
}

@injectable()
export class SensorGroupComponentViewmodel extends BaseViewModel {
    @observable
    sensorGroups: SensorGroups = SensorGroups.empty();

    @observable
    currentStation: Readonly<Station> | null = null;

    @observable
    alertsBySensorId = new Map<number, { low?: number; high?: number }>();

    @observable
    initialLoading = true;

    @observable
    modalGraphProps: ModalGraphProps = {
        visible: false,
        sensors: [],
        yAxes: [],
    };

    constructor(
        @inject(SensorGroupGetAllUseCase)
        private readonly sensorGroupGetAllUseCase: SensorGroupGetAllUseCase,
        @inject(SensorAlertGetAllUseCase)
        private readonly sensorAlertGetAllUseCase: SensorAlertGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    async initViewData(): Promise<void> {
        await this.fetchSensorGroups();
        await this.fetchAlerts();

        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    private async fetchSensorGroups(): Promise<void> {
        if (!this.currentStation) throw new Error("Current station is not set");

        const sensorGroups = await this.sensorGroupGetAllUseCase.execute(
            this.currentStation.id.value,
        );
        runInAction(() => {
            this.sensorGroups = sensorGroups;
        });
    }

    @action
    async fetchAlerts(): Promise<void> {
        if (!this.currentStation) return;

        const result = await this.sensorAlertGetAllUseCase.execute(
            this.currentStation.id.value,
        );

        const map = new Map<number, { low?: number; high?: number }>();
        result.sensorAlerts.forEach((alert) => {
            alert.variableAlerts.forEach((variableAlert) => {
                map.set(variableAlert.sensor.id, {
                    low: variableAlert.lowTriggerValue ?? undefined,
                    high: variableAlert.highTriggerValue ?? undefined,
                });
            });
        });

        runInAction(() => {
            this.alertsBySensorId = map;
        });
    }

    @action
    setStation(station: Readonly<Station>): void {
        this.currentStation = station;
    }

    @action
    setModalGraphProps = (props: ModalGraphProps): void => {
        this.modalGraphProps = props;
    };
}
